import React from 'react';
import { PanelLeftClose, PanelLeftOpen, LogOut } from 'lucide-react';
import config from '../../configLoader';
import { useSiteContext } from '../../SiteContext';
import { Page } from '../../interfaces/PagesInterface';
import { getVisibleGroups, HOME_ALIAS_PAGES, NavItem } from './navConfig';

interface SidebarV2Props {
  collapsed: boolean;
  onToggleCollapse: () => void;
  onSignOut: () => void;
}

const SidebarV2: React.FC<SidebarV2Props> = ({ collapsed, onToggleCollapse, onSignOut }) => {
  const { userRole, miscTaskApprovalConfigs, page, setPage, bumpReload, translator, pendingTimeOffCount, employeeMode, portalBadges } =
    useSiteContext();

  // Navigate to the item's page. Clicking the item for the page you're already on is
  // otherwise a no-op (setPage to the same value), so bump the reload nonce to remount it.
  const handleNavClick = (target: Page) => {
    setPage(target);
    bumpReload();
  };

  const groups = getVisibleGroups({
    userRole, config, miscTaskApprovalConfigs, experience: employeeMode ? 'employee' : 'admin',
  });

  /** The badge for an item: [count, amber?]; 0 = none. */
  const badgeFor = (item: NavItem): [number, boolean] => {
    switch (item.badge) {
      case 'pendingTimeOff': return [pendingTimeOffCount, false];
      case 'myTimesheet': return [portalBadges.timesheet, true];
      case 'myTimeOff': return [portalBadges.timeOff, false];
      default: return [0, false];
    }
  };

  const labelFor = (item: NavItem): string =>
    item.label ?? translator.translate(item.labelKey ?? item.page);

  const isActive = (item: NavItem): boolean => {
    if (item.page === Page.Dashboard) return HOME_ALIAS_PAGES.includes(page);
    return page === item.page;
  };

  return (
    <div className="side">
      <nav className="side-nav">
        {groups.map((group) => (
          <div className="nav-group" key={group.label}>
            <div className="nav-glabel">{group.label}</div>
            {group.items.map((item) => {
              const Icon = item.icon;
              const [badgeCount, badgeWarn] = badgeFor(item);
              return (
                <button
                  key={item.page}
                  type="button"
                  className={`nav-item${isActive(item) ? ' active' : ''}`}
                  onClick={() => handleNavClick(item.page)}
                  title={collapsed ? labelFor(item) : undefined}
                >
                  <Icon className="nav-ic" size={18} />
                  <span className="nav-tx">{labelFor(item)}</span>
                  {badgeCount > 0 && <span className={`nav-badge${badgeWarn ? ' warn' : ''}`}>{badgeCount}</span>}
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="side-foot">
        <button type="button" className="side-toggle" onClick={onToggleCollapse}>
          {collapsed ? (
            <PanelLeftOpen className="side-toggle-ic" size={18} />
          ) : (
            <PanelLeftClose className="side-toggle-ic" size={18} />
          )}
          <span className="so-tx">{collapsed ? 'Expand menu' : 'Collapse menu'}</span>
        </button>
        <button type="button" className="signout" onClick={onSignOut}>
          <LogOut className="side-toggle-ic" size={17} />
          <span className="so-tx">{translator.translate('SignOut')}</span>
        </button>
      </div>
    </div>
  );
};

export default SidebarV2;
